import { useState } from "react";
import { KeyRound } from "lucide-react";
import { useI18n, serverError } from "../i18n";
import type { AdminSession } from "../App";

/**
 * 帳號設定：改自己的密碼
 * ═══════════════════════════════════════════════════════════════
 *
 * 在這之前唯一的辦法是在終端機跑 scripts/reset-admin-password.mjs。
 * 那支腳本仍然留著（忘記密碼、進不去後台時的救援），但
 * 「我記得舊密碼、只是想換一組」不該需要一台裝了 wrangler 的電腦。
 *
 * ── 為什麼要輸入目前的密碼 ──────────────────────────────────
 *
 * 沒有這一步的話，任何拿到一個還開著的分頁的人都能把密碼換掉，
 * 然後把真正的管理員鎖在外面。驗舊密碼把「暫時碰得到瀏覽器」
 * 和「真的是本人」分開。
 */
export default function SettingsAccount({ session }: { session: AdminSession }) {
  const { t } = useI18n();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = again.length > 0 && next !== again;
  const ready = current.length > 0 && next.length >= 12 && next === again && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;

    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/me/password", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const payload = await res.json().catch(() => null);

      if (!res.ok) {
        setError(serverError(payload, t, t("acct.failed")));
        return;
      }

      setDone(true);
      setCurrent("");
      setNext("");
      setAgain("");
    } catch {
      setError(t("acct.network"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <section className="rounded-card nm p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <KeyRound className="size-4 text-brand-300" />
          {t("acct.password")}
        </h2>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">{t("acct.lede")}</p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {/*
            隱藏的帳號欄位。

            瀏覽器在 console 抱怨「密碼表單應該要有帳號欄位」不是
            吹毛求疵：密碼管理員靠它判斷這組密碼屬於哪個帳號，
            沒有它就存成一筆沒有主人的密碼，之後自動填入會填錯。
          */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={session.email}
            readOnly
            hidden
          />

          <Field label={t("acct.current")} htmlFor="current">
            <input
              id="current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputClass}
            />
          </Field>

          <Field
            label={t("acct.new")}
            htmlFor="new"
            hint={t("acct.minLength")}
            error={tooShort ? t("acct.tooShort") : undefined}
          >
            <input
              id="new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputClass}
            />
          </Field>

          <Field
            label={t("acct.confirm")}
            htmlFor="again"
            error={mismatch ? t("acct.mismatch") : undefined}
          >
            <input
              id="again"
              type="password"
              autoComplete="new-password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              className={inputClass}
            />
          </Field>

          {error && (
            <p className="rounded-xl nm-inset px-4 py-3 text-xs leading-relaxed text-brand-300" role="alert">
              {error}
            </p>
          )}

          {done && (
            <p className="rounded-xl nm-inset px-4 py-3 text-xs leading-relaxed text-ink-soft" role="status">
              {t("acct.done")}
            </p>
          )}

          <button
            type="submit"
            disabled={!ready}
            className="rounded-pill nm-pressable px-5 py-2.5 text-sm font-semibold text-ink disabled:opacity-40"
          >
            {busy ? t("acct.saving") : t("acct.submit")}
          </button>
        </form>

        <p className="mt-5 border-t border-white/5 pt-4 text-[11px] leading-relaxed text-ink-muted">
          {t("acct.note")}
        </p>
      </section>

      <section className="rounded-card nm-inset p-5">
        <h3 className="text-xs font-semibold text-ink">{t("acct.lockedOut")}</h3>
        <p className="mt-1.5 text-[11px] leading-relaxed text-ink-muted">
          {t("acct.lockedOutBody")}
        </p>
        <code className="mt-2 block rounded-lg bg-black/25 px-3 py-2 text-[11px] text-ink-soft">
          node --experimental-strip-types scripts/reset-admin-password.mjs
        </code>
      </section>
    </div>
  );
}

const inputClass =
  "w-full rounded-xl nm-inset bg-transparent px-4 py-2.5 text-sm text-ink outline-none " +
  "focus:ring-1 focus:ring-brand-400/40";

function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-xs font-medium text-ink-soft">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {error ? (
        <p className="mt-1.5 text-[11px] text-brand-300">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-[11px] text-ink-muted">{hint}</p>
      ) : null}
    </div>
  );
}
